import { Level } from '@susu/custom-made/utils/constants';
import { Section } from '@susu/custom-made/utils/navigation';
import type { LayerDefinition } from '@susu/custom-made/utils/preview/layers';
import { checkRotationStep, rotationConfig } from '@susu/custom-made/utils/utils';
import type { SyntheticEvent } from 'react';
import React from 'react';

import { getPreviewLayerClasses } from './previewLayer.classes';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';

const { ENABLE_ROTATION_ANIMATION } = FEATURE_TOGGLES;

const PreviewRotationLayer = React.memo(
  ({
    layer,
    index,
    keyPrefix,
    onLayerFailed,
    onLayerLoaded,
  }: Readonly<{
    layer: LayerDefinition;
    index: number;
    keyPrefix: string;
    onLayerFailed: (e: SyntheticEvent<HTMLImageElement, Event>) => void;
    onLayerLoaded: (e: SyntheticEvent<HTMLImageElement, Event>) => void;
  }>) => {
    const { section, level } = useNavigationContext();
    const { animation, selectedOption } = useOptionContext();
    const enableRotationAnimationFeatureEnabled = useFeatureIsOn(ENABLE_ROTATION_ANIMATION);
    
    return (
      <>
        {Object.entries(rotationConfig).map(([key, value]) => (
          <div
            className={getPreviewLayerClasses(
              !animation.status &&
                (((level === Level.Group || section === Section.Fabric) && key === 'front') ||
                  (level === Level.Subgroup &&
                    section !== Section.Fabric &&
                    checkRotationStep(selectedOption).spriteStep === value.spriteStep)),
            )}
            key={`${keyPrefix}_${index}_${key}`}
          >
            <img
              src={layer.srcRotation.replaceAll('{rotation}', value.imageSuffix)}
              srcSet={layer.srcsetRotation.replaceAll('{rotation}', value.imageSuffix)}
              alt={`${keyPrefix}_${index}_rotation`}
              onError={onLayerFailed}
              onLoad={onLayerLoaded}
              sizes={layer.sizes}
              fetchPriority="high"
              loading="eager"
            />
          </div>
        ))}
        {enableRotationAnimationFeatureEnabled && (
          <div className={getPreviewLayerClasses(animation.status, true)}>
            <img
              src={layer.srcSheet}
              alt={`${keyPrefix}_${index}_sheet`}
              onError={onLayerFailed}
              onLoad={onLayerLoaded}
              sizes={layer.sizes}
              fetchPriority="high"
              loading="eager"
            />
          </div>
        )}
      </>
    );
  },
);

PreviewRotationLayer.displayName = 'PreviewRotationLayer';

export default PreviewRotationLayer;
